import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { auth, db } from "../firebase/config";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { useAuthState } from "react-firebase-hooks/auth";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { toast } from "react-toastify";

// Pozisyon başlıkları için anahtar kelimeler
const positionTitles = [
  "Waiter/Waitress",
  "Dishwasher",
  "Cashier",
  "Kitchen Helper/Prep Cook",
  "Hostess",
  "Barista",
  "Front Desk Assistant",
  "Housekeeper",
  "Bellhop",
  "Pool Attendant",
  "Game Attendant",
  "Ride Operator",
  "Ticket Seller",
  "Waterpark Attendant",
  "Concession Stand Worker",
  "Sales Associate",
  "Photographer",
  "Busser",
  "Food Runner",
  "Other",
  "Server",
];

const CreateListing = () => {
  const [user] = useAuthState(auth);
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [listingType, setListingType] = useState("job"); // job veya housing

  const [formData, setFormData] = useState({
    type: "job",
    title: "",
    companyName: "",
    jobType: "full-time",
    address: {
      street: "",
      aptNo: "",
      city: "Myrtle Beach",
      state: "SC",
      zipCode: "",
    },
    salary: "",
    contactName: "",
    contactSurname: "",
    contactPhone: "",
    contactEmail: "",
    facebookUsername: "",
    description: "",
    status: "active",
    whatsappAvailable: false,
    // Job spesifik alanlar
    tipsIncluded: false,
    transportationProvided: false,
    overtimeAvailable: false,
    housingProvided: false,
    englishLevel: "basic",
    // Housing spesifik alanlar
    rent: "",
    deposit: "",
    utilities: "",
    roomType: "private",
    availableFrom: "",
    availableUntil: "",
    furnished: false,
    internetIncluded: false,
    images: [],
  });

  const [suggestions, setSuggestions] = useState([]);

  const handleTitleChange = (e) => {
    const { value } = e.target;
    setFormData((prev) => ({
      ...prev,
      title: value,
    }));

    if (value.length > 0) {
      const filteredSuggestions = positionTitles.filter((title) =>
        title.toLowerCase().includes(value.toLowerCase())
      );
      setSuggestions(filteredSuggestions);
    } else {
      setSuggestions([]);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setFormData((prev) => ({
      ...prev,
      title: suggestion,
    }));
    setSuggestions([]);
  };

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files) return;

    try {
      setLoading(true);
      const storage = getStorage();
      const uploadedImages = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const storageRef = ref(storage, `images/${Date.now()}-${file.name}`);
        await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(storageRef);
        uploadedImages.push(downloadURL);
      }

      setFormData((prev) => ({
        ...prev,
        images: [...prev.images, ...uploadedImages],
      }));
      toast.success("Images uploaded successfully!");
    } catch (error) {
      console.error("Error uploading images:", error);
      toast.error("Failed to upload images");
    } finally {
      setLoading(false);
    }
  };

  const handleImageDelete = (indexToDelete) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, index) => index !== indexToDelete),
    }));
    toast.success("Image removed successfully!");
  };

  // Telefon numarası formatı için yardımcı fonksiyon
  const formatPhoneNumber = (value) => {
    // Sadece rakamları al
    const numbers = value.replace(/\D/g, "");

    // Maksimum 10 rakam
    if (numbers.length > 10) return formData.contactPhone;

    // Format: (XXX) XXX-XXXX
    let formattedNumber = "";
    if (numbers.length >= 1) {
      formattedNumber += "(" + numbers.slice(0, 3);
      if (numbers.length >= 4) {
        formattedNumber += ") " + numbers.slice(3, 6);
        if (numbers.length >= 7) {
          formattedNumber += "-" + numbers.slice(6, 10);
        }
      }
    }
    return formattedNumber;
  };

  // Telefon numarası validasyonu için fonksiyon
  const isValidPhoneNumber = (phone) => {
    const phoneRegex = /^\(\d{3}\)\s\d{3}-\d{4}$/;
    return phoneRegex.test(phone);
  };

  // Adres validasyonu için fonksiyon
  const isValidAddress = (address) => {
    return address.street.length >= 5 && address.zipCode.length === 5;
  };

  // Description validasyonu için fonksiyon ekle
  const isValidDescription = (description) => {
    return description.length >= 10;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "contactPhone") {
      const formattedPhone = formatPhoneNumber(value);
      setFormData((prev) => ({
        ...prev,
        [name]: formattedPhone,
      }));

      // Validasyon kontrolü
      if (formattedPhone.length === 14 && !isValidPhoneNumber(formattedPhone)) {
        setValidationErrors((prev) => ({
          ...prev,
          contactPhone:
            "Please enter a valid phone number in format: (XXX) XXX-XXXX",
        }));
      } else {
        setValidationErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.contactPhone;
          return newErrors;
        });
      }
      return;
    }

    // Adres alanları için özel işlem
    if (name.startsWith("address.")) {
      const addressField = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value,
        },
      }));

      // Adres validasyonunu kontrol et
      const newAddress = {
        ...formData.address,
        [addressField]: value,
      };

      if (!isValidAddress(newAddress)) {
        setValidationErrors((prev) => ({
          ...prev,
          address: "Please fill in the required address fields",
        }));
      } else {
        setValidationErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.address;
          return newErrors;
        });
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));

      // Validasyon hatalarını temizle
      if (name === "contactPhone") {
        if (!isValidPhoneNumber(value)) {
          setValidationErrors((prev) => ({
            ...prev,
            contactPhone:
              "Please enter a valid US phone number (e.g., (843) 123-4567)",
          }));
        } else {
          setValidationErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors.contactPhone;
            return newErrors;
          });
        }
      } else {
        setValidationErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[name];
          return newErrors;
        });
      }

      // Description kontrolünü ekle
      if (name === "description") {
        if (!isValidDescription(value)) {
          setValidationErrors((prev) => ({
            ...prev,
            description: "Job description must be at least 10 characters long",
          }));
        } else {
          setValidationErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors.description;
            return newErrors;
          });
        }
      }
    }
  };

  const resetForm = () => {
    setFormData({
      type: listingType,
      title: "",
      companyName: "",
      jobType: "full-time",
      address: {
        street: "",
        aptNo: "",
        city: "Myrtle Beach",
        state: "SC",
        zipCode: "",
      },
      salary: "",
      contactName: "",
      contactSurname: "",
      contactPhone: "",
      contactEmail: "",
      facebookUsername: "",
      description: "",
      status: "active",
      whatsappAvailable: false,
      // Job spesifik alanlar
      tipsIncluded: false,
      transportationProvided: false,
      overtimeAvailable: false,
      housingProvided: false,
      englishLevel: "basic",
      // Housing spesifik alanlar
      rent: "",
      deposit: "",
      utilities: "",
      roomType: "private",
      availableFrom: "",
      availableUntil: "",
      furnished: false,
      internetIncluded: false,
      images: [],
    });
    setValidationErrors({});
    setError("");
  };

  const handleListingTypeChange = (type) => {
    setListingType(type);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Form validation checks
    const validationErrors = {};

    // Description length check
    if (formData.description.length < 10) {
      validationErrors.description =
        "Description must be at least 10 characters long";
    }

    // Phone number check
    if (!isValidPhoneNumber(formData.contactPhone)) {
      validationErrors.contactPhone =
        "Please enter a valid phone number in format: (XXX) XXX-XXXX";
    }

    // If there are any validation errors, stop form submission
    if (Object.keys(validationErrors).length > 0) {
      setValidationErrors(validationErrors);
      toast.error("Please fix the errors before submitting");
      return;
    }

    setLoading(true);

    try {
      if (!user) throw new Error("Please login to create a listing");

      const fullAddress = [
        formData.address.street,
        formData.address.aptNo,
        formData.address.city,
        formData.address.state,
        formData.address.zipCode,
      ]
        .filter(Boolean) // Remove empty values
        .join(", ")
        .replace(/\s+/g, " ") // Remove extra spaces
        .trim();

      const tags = [];
      if (formData.tipsIncluded) tags.push("Tips Available");
      if (formData.transportationProvided) tags.push("Transportation Provided");

      const listingData = {
        ...formData,
        location: fullAddress,
        userId: user.uid,
        userEmail: user.email,
        createdAt: serverTimestamp(),
        status: "active",
        listingType: listingType,
        tags: tags,
      };

      await addDoc(collection(db, "listings"), listingData);
      navigate(listingType === "job" ? "/jobs" : "/housing");
    } catch (err) {
      console.error("Error creating listing:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='min-h-screen bg-[#F8F9FA] py-8'>
      <div className='max-w-2xl mx-auto px-4'>
        <div className='bg-white rounded-lg shadow-lg p-8'>
          {/* Tab Buttons */}
          <div className='flex gap-0 mb-8'>
            <button
              onClick={() => handleListingTypeChange("job")}
              className={`w-1/2 py-4 text-xl font-chirp font-semibold border-2 border-blue-600 rounded-l transition
                ${listingType === "job" ? "bg-blue-100" : "hover:bg-blue-50"}`}>
              Job Listing
            </button>
            <button
              onClick={() => handleListingTypeChange("housing")}
              className={`w-1/2 py-4 text-xl font-chirp font-semibold border-2 border-l-0 border-blue-600 rounded-r transition
                ${
                  listingType === "housing" ? "bg-blue-100" : "hover:bg-blue-50"
                }`}>
              Housing Listing
            </button>
          </div>

          <h1 className='text-3xl font-semibold text-gray-800 mb-8 text-center'>
            Create {listingType === "job" ? "Job" : "Housing"} Listing
          </h1>

          {error && (
            <div className='bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-6 text-base font-medium'>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className='space-y-6'>
            {listingType === "job" ? (
              // Job Listing Form Fields
              <>
                {/* Pozisyon Adı */}
                <div className='relative'>
                  <label className='block text-gray-700 font-medium text-base mb-2'>
                    Position Title
                  </label>
                  <input
                    type='text'
                    name='title'
                    value={formData.title}
                    onChange={handleTitleChange}
                    placeholder='e.g. Waiter, Kitchen Helper'
                    className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500'
                    required
                  />
                  {suggestions.length > 0 && (
                    <div className='absolute z-10 w-full bg-white border border-gray-300 rounded-lg mt-1 max-h-48 overflow-y-auto'>
                      {suggestions.map((suggestion, index) => (
                        <div
                          key={index}
                          className='px-4 py-2 hover:bg-gray-100 cursor-pointer'
                          onClick={() => handleSuggestionClick(suggestion)}>
                          {suggestion}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Company Name */}
                <div>
                  <label className='block text-gray-700 font-medium text-base mb-2'>
                    Company Name
                  </label>
                  <input
                    type='text'
                    name='companyName'
                    value={formData.companyName}
                    onChange={(e) => {
                      const upperCaseValue = e.target.value.toUpperCase();
                      setFormData((prev) => ({
                        ...prev,
                        companyName: upperCaseValue,
                      }));
                    }}
                    placeholder='Enter your company name'
                    className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500'
                    required
                  />
                </div>

                {/* Job Type */}
                <div>
                  <label className='block text-gray-700 font-medium text-base mb-2'>
                    Job Type
                  </label>
                  <select
                    name='jobType'
                    value={formData.jobType}
                    onChange={handleChange}
                    className='w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base'
                    required>
                    <option value='full-time'>Full Time</option>
                    <option value='part-time'>Part Time</option>
                    <option value='seasonal'>Seasonal</option>
                    <option value='weekends'>Weekends Only</option>
                  </select>
                </div>

                {/* Salary */}
                <div>
                  <label className='block text-gray-700 font-medium text-base mb-2'>
                    Salary (per hour)
                  </label>
                  <input
                    type='number'
                    name='salary'
                    value={formData.salary}
                    onChange={handleChange}
                    placeholder='e.g. 15'
                    className='w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base'
                    required
                  />
                </div>

                {/* Job spesifik alanlar */}
                <div className='space-y-4'>
                  <div className='flex flex-wrap gap-4'>
                    <label className='flex items-center space-x-2'>
                      <input
                        type='checkbox'
                        checked={formData.tipsIncluded}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            tipsIncluded: e.target.checked,
                          }))
                        }
                        className='form-checkbox h-4 w-4 text-blue-600'
                      />
                      <span>Tips Included</span>
                    </label>

                    <label className='flex items-center space-x-2'>
                      <input
                        type='checkbox'
                        checked={formData.transportationProvided}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            transportationProvided: e.target.checked,
                          }))
                        }
                        className='form-checkbox h-4 w-4 text-blue-600'
                      />
                      <span>Transportation Provided</span>
                    </label>

                    <label className='flex items-center space-x-2'>
                      <input
                        type='checkbox'
                        checked={formData.overtimeAvailable}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            overtimeAvailable: e.target.checked,
                          }))
                        }
                        className='form-checkbox h-4 w-4 text-blue-600'
                      />
                      <span>Overtime Available</span>
                    </label>

                    <label className='flex items-center space-x-2'>
                      <input
                        type='checkbox'
                        checked={formData.housingProvided}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            housingProvided: e.target.checked,
                          }))
                        }
                        className='form-checkbox h-4 w-4 text-blue-600'
                      />
                      <span>Housing Provided</span>
                    </label>
                  </div>

                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-1'>
                      Required English Level
                    </label>
                    <select
                      name='englishLevel'
                      value={formData.englishLevel}
                      onChange={handleChange}
                      className='mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm'>
                      <option value='basic'>Basic</option>
                      <option value='intermediate'>Intermediate</option>
                      <option value='advanced'>Advanced</option>
                      <option value='fluent'>Fluent</option>
                    </select>
                  </div>
                </div>
              </>
            ) : (
              // Housing Listing Form Fields
              <>
                {/* İlan Başlığı */}
                <div>
                  <label className='block text-gray-700 font-medium text-base mb-2'>
                    Listing Title
                  </label>
                  <input
                    type='text'
                    name='title'
                    value={formData.title}
                    onChange={handleChange}
                    placeholder='e.g. Cozy Room in Shared Apartment'
                    className='w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base'
                    required
                  />
                </div>

                {/* Oda Tipi */}
                <div>
                  <label className='block text-gray-700 font-medium text-base mb-2'>
                    Room Type
                  </label>
                  <select
                    name='roomType'
                    value={formData.roomType}
                    onChange={handleChange}
                    className='w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base'
                    required>
                    <option value='private'>Private Room</option>
                    <option value='shared'>Shared Room</option>
                    <option value='studio'>Studio Apartment</option>
                    <option value='apartment'>Full Apartment</option>
                  </select>
                </div>

                {/* Kira */}
                <div>
                  <label className='block text-gray-700 font-medium text-base mb-2'>
                    Weekly Rent
                  </label>
                  <input
                    type='number'
                    name='rent'
                    value={formData.rent}
                    onChange={handleChange}
                    placeholder='e.g. 800'
                    className='w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base'
                    required
                  />
                </div>

                {/* Depozito */}
                <div>
                  <label className='block text-gray-700 font-medium text-base mb-2'>
                    Security Deposit
                  </label>
                  <input
                    type='number'
                    name='deposit'
                    value={formData.deposit}
                    onChange={handleChange}
                    placeholder='e.g. 800'
                    className='w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base'
                    required
                  />
                </div>

                {/* Fotoğraf Yükleme */}
                <div className='space-y-2'>
                  <label className='block text-gray-700 font-medium text-base mb-2'>
                    Upload Photos
                  </label>
                  <input
                    type='file'
                    accept='image/*'
                    multiple
                    onChange={handleImageUpload}
                    className='w-full p-2 border rounded-lg'
                  />
                  {formData.images.length > 0 && (
                    <div className='grid grid-cols-2 gap-2 mt-2'>
                      {formData.images.map((url, index) => (
                        <div key={index} className='relative'>
                          <img
                            src={url}
                            alt={`Uploaded ${index + 1}`}
                            className='w-full h-40 object-cover rounded-lg'
                          />
                          <button
                            type='button'
                            onClick={() => handleImageDelete(index)}
                            className='absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 focus:outline-none'>
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Availability Dates */}
                <div className='grid grid-cols-2 gap-4'>
                  <div>
                    <label className='block text-gray-700 font-medium text-base mb-2'>
                      Available From
                    </label>
                    <input
                      type='date'
                      name='availableFrom'
                      value={formData.availableFrom}
                      onChange={handleChange}
                      className='w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base'
                      required
                    />
                  </div>
                  <div>
                    <label className='block text-gray-700 font-medium text-base mb-2'>
                      Available Until
                    </label>
                    <input
                      type='date'
                      name='availableUntil'
                      value={formData.availableUntil}
                      onChange={handleChange}
                      className='w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base'
                      required
                    />
                  </div>
                </div>

                {/* Amenities */}
                <div className='space-y-3'>
                  <label className='block text-gray-700 font-medium text-base mb-2'>
                    Amenities
                  </label>
                  <div className='flex items-center space-x-2'>
                    <input
                      type='checkbox'
                      name='furnished'
                      checked={formData.furnished}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          furnished: e.target.checked,
                        }))
                      }
                      className='w-4 h-4 text-blue-600'
                    />
                    <span>Furnished</span>
                  </div>
                  <div className='flex items-center space-x-2'>
                    <input
                      type='checkbox'
                      name='internetIncluded'
                      checked={formData.internetIncluded}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          internetIncluded: e.target.checked,
                        }))
                      }
                      className='w-4 h-4 text-blue-600'
                    />
                    <span>Internet Included</span>
                  </div>
                </div>
              </>
            )}

            {/* Common Fields */}
            {/* Adres Bilgileri */}
            <div className='space-y-6'>
              <h3 className='text-xl font-semibold text-gray-800'>
                Address Information
              </h3>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <div>
                  <label className='block text-gray-700 font-medium text-base mb-2'>
                    Street
                  </label>
                  <input
                    type='text'
                    name='address.street'
                    value={formData.address.street}
                    onChange={handleChange}
                    placeholder='Street address'
                    className='w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base'
                    required
                  />
                </div>
                <div>
                  <label className='block text-gray-700 font-medium text-base mb-2'>
                    Apt/Suite No
                  </label>
                  <input
                    type='text'
                    name='address.aptNo'
                    value={formData.address.aptNo}
                    onChange={handleChange}
                    placeholder='Apartment or suite number'
                    className='w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base'
                  />
                </div>
              </div>
              <div>
                <label className='block text-gray-700 font-medium text-base mb-2'>
                  ZIP Code
                </label>
                <input
                  type='text'
                  name='address.zipCode'
                  value={formData.address.zipCode}
                  onChange={handleChange}
                  placeholder='ZIP Code'
                  className='w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base'
                  required
                />
              </div>
              {validationErrors.address && (
                <p className='text-red-500 text-sm'>
                  {validationErrors.address}
                </p>
              )}
            </div>

            {/* İletişim Bilgileri */}
            <div className='space-y-6'>
              <h3 className='text-xl font-semibold text-gray-800'>
                Contact Information
              </h3>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <div>
                  <label className='block text-gray-700 font-medium text-base mb-2'>
                    First Name
                  </label>
                  <input
                    type='text'
                    name='contactName'
                    value={formData.contactName}
                    onChange={handleChange}
                    className='w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base'
                    required
                  />
                </div>
                <div>
                  <label className='block text-gray-700 font-medium text-base mb-2'>
                    Last Name
                  </label>
                  <input
                    type='text'
                    name='contactSurname'
                    value={formData.contactSurname}
                    onChange={handleChange}
                    className='w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base'
                    required
                  />
                </div>
              </div>
              <div>
                <label className='block text-gray-700 font-medium text-base mb-2'>
                  Phone Number
                </label>
                <input
                  type='tel'
                  name='contactPhone'
                  value={formData.contactPhone}
                  onChange={handleChange}
                  placeholder='(843) XXX-XXXX'
                  className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base ${
                    validationErrors.contactPhone
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  required
                />
                {validationErrors.contactPhone && (
                  <p className='text-red-500 text-sm mt-1'>
                    {validationErrors.contactPhone}
                  </p>
                )}
                <p className='text-sm text-gray-500 mt-1'>
                  Format: (843) XXX-XXXX
                </p>
              </div>
              <div className='flex items-center space-x-2'>
                <input
                  type='checkbox'
                  name='whatsappAvailable'
                  checked={formData.whatsappAvailable}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      whatsappAvailable: e.target.checked,
                    }))
                  }
                  className='w-4 h-4 text-blue-600'
                />
                <span className='text-gray-700'>
                  WhatsApp available for communication
                </span>
              </div>
              <div>
                <label className='block text-gray-700 font-medium text-base mb-2'>
                  Email
                </label>
                <input
                  type='email'
                  name='contactEmail'
                  value={formData.contactEmail}
                  onChange={handleChange}
                  className='w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base'
                  required
                />
              </div>
              <div>
                <label className='block text-gray-700 font-medium text-base mb-2'>
                  Facebook Username
                </label>
                <input
                  type='text'
                  name='facebookUsername'
                  value={formData.facebookUsername}
                  onChange={handleChange}
                  className='w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base'
                  required
                />
              </div>
              <div>
                <p className='text-sm text-gray-500 mt-1'>
                  Just enter your username, not the full URL
                </p>
              </div>
            </div>

            {/* Açıklama */}
            <div>
              <label className='block text-gray-700 font-medium text-base mb-2'>
                Description
              </label>
              <textarea
                name='description'
                value={formData.description}
                onChange={handleChange}
                rows={4}
                className='w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base'
                required
              />
              {validationErrors.description && (
                <p className='text-red-500 text-sm'>
                  {validationErrors.description}
                </p>
              )}
            </div>

            {/* Submit Button */}
            <div>
              <button
                type='submit'
                disabled={loading}
                className='w-full py-3 px-4 border border-transparent rounded-lg shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 font-medium text-lg disabled:opacity-50'>
                {loading
                  ? "Creating..."
                  : `Create ${
                      listingType === "job" ? "Job" : "Housing"
                    } Listing`}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateListing;
